import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { PageShell } from '../components/PageShell';
import { KindIcon } from '../components/KindIcon';
import { DepartmentSelect } from '../components/DepartmentSelect';
import { colors, cardStyle } from '../theme';
import { CREATE_KIND_CONFIG } from '../types';
import type { WorkflowStatus } from '../types';
import { PROGRESS_STAGES, stageIndexForStatus } from '../createFlowConfig';
import { sanitizeName } from '../nameUtils';
import { inputStyle, labelStyle, Field, ProgressStepper } from '../formPrimitives';
import { useIdentity } from '../useIdentity';
import { createAndDeployApplication, getWorkflowStatus, checkAppNameAvailability } from '../api';

// A dedicated create flow for AI Agents — same visual language as every
// other /create/* page (KindIcon header, cardStyle card, shared
// input/label/button styles from formPrimitives) but its own field set:
// Describe / Knowledge / Tools / AI Model, with no separate name field —
// the repo slug is derived from the agent's own description instead.

const TOOLS = ['Slack', 'Jira', 'Email', 'Database', 'Web'] as const;
type Tool = (typeof TOOLS)[number];

/**
 * Secret env-var names each tool needs at runtime, declared on the manifest as
 * `aws.secrets` so the deploy workflow resolves them from Floci Secrets
 * Manager. The token values are NEVER collected by this form and never put in
 * `extraEnvVars` — those become plainly-readable Coolify env vars, visible in
 * the Application Details "Infrastructure" tab. Only non-secret configuration
 * (a project key, a channel name) travels that way.
 *
 * Names are SCREAMING_SNAKE_CASE to satisfy the manifest's SecretRef pattern,
 * and match what templates/ai-agent-python/app/tools/ actually reads.
 */
const TOOL_SECRETS: Partial<Record<Tool, string[]>> = {
  Jira: ['JIRA_API_TOKEN'],
  Slack: ['SLACK_BOT_TOKEN'],
};

const MODEL_OPTIONS = [
  { value: 'byok', label: 'BYOK - Bring Your Own Key' },
  { value: 'company-managed', label: 'Company Managed' },
] as const;

function deriveSlug(description: string): string {
  const words = description.trim().split(/\s+/).filter(Boolean).slice(0, 6).join(' ');
  return sanitizeName(words);
}

/** A "+ Upload PDF"-style text link that turns into a "✓ filename" confirmation
 * once a value is picked — same idiom as EnvVarsEditor's "+ Add variable". */
function KnowledgeButton({
  label,
  doneLabel,
  onClick,
  ariaLabel,
}: {
  label: string;
  doneLabel?: string;
  onClick: () => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        display: 'block',
        border: 'none',
        background: 'transparent',
        color: doneLabel ? colors.success : colors.primary,
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 600,
        padding: 0,
        textAlign: 'left',
      }}
    >
      {doneLabel ?? label}
    </button>
  );
}

export function CreateAiAgentPage() {
  const navigate = useNavigate();
  const { displayName } = useIdentity();
  const identityApi = useApi(identityApiRef);
  const kindConfig = CREATE_KIND_CONFIG['ai-agent'];
  const template = kindConfig.template!;

  const [department, setDepartment] = useState('');
  const [description, setDescription] = useState('');
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [excelFileName, setExcelFileName] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState<string | null>(null);
  const [editingWebsite, setEditingWebsite] = useState(false);
  const [websiteDraft, setWebsiteDraft] = useState('');
  const [tools, setTools] = useState<Set<Tool>>(new Set());
  // Non-secret per-tool configuration. Revealed only when its tool is ticked.
  const [jiraProjectKey, setJiraProjectKey] = useState('');
  const [slackChannel, setSlackChannel] = useState('');
  const [modelOption, setModelOption] = useState<(typeof MODEL_OPTIONS)[number]['value']>('company-managed');
  const [byokApiKey, setByokApiKey] = useState('');
  const [status, setStatus] = useState<WorkflowStatus>({ phase: 'idle' });

  const pdfInputRef = useRef<HTMLInputElement>(null);
  const excelInputRef = useRef<HTMLInputElement>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout>>();

  type NameCheckState = { phase: 'idle' | 'checking' | 'available' | 'taken' | 'error' };
  const [nameCheck, setNameCheck] = useState<NameCheckState>({ phase: 'idle' });
  const nameCheckTimer = useRef<ReturnType<typeof setTimeout>>();

  const sanitizedName = deriveSlug(description);

  useEffect(() => () => clearTimeout(pollTimer.current), []);

  // Same debounced live-availability check the generic flow uses, keyed off
  // the derived slug instead of a typed name field — see CreateFlowPage.tsx.
  useEffect(() => {
    clearTimeout(nameCheckTimer.current);
    if (!sanitizedName) {
      setNameCheck({ phase: 'idle' });
      return;
    }
    setNameCheck({ phase: 'checking' });
    nameCheckTimer.current = setTimeout(async () => {
      try {
        const result = await checkAppNameAvailability(identityApi, sanitizedName, department || undefined);
        setNameCheck({ phase: result.available ? 'available' : 'taken' });
      } catch {
        setNameCheck({ phase: 'error' });
      }
    }, 500);
    return () => clearTimeout(nameCheckTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sanitizedName, department]);

  const toggleTool = (tool: Tool) => {
    setTools((prev) => {
      const next = new Set(prev);
      if (next.has(tool)) next.delete(tool);
      else next.add(tool);
      return next;
    });
  };

  const commitWebsite = () => {
    const trimmed = websiteDraft.trim();
    setWebsiteUrl(trimmed || null);
    setEditingWebsite(false);
  };

  const poll = useCallback(
    (workflowId: string) => {
      const tick = async () => {
        try {
          const res = await getWorkflowStatus(identityApi, workflowId);
          if (res.result) {
            setStatus({ phase: 'done', result: res.result });
            return;
          }
          setStatus({ phase: 'polling', workflowId, status: res.status });
          pollTimer.current = setTimeout(tick, 2000);
        } catch (err) {
          setStatus({ phase: 'error', message: (err as Error).message });
        }
      };
      tick();
    },
    [identityApi],
  );

  const onSubmit = async () => {
    if (!sanitizedName) return;
    setStatus({ phase: 'submitting' });

    const extraEnvVars: Record<string, string> = { MUMZWORLD_MODEL_PROVIDER: modelOption };
    if (modelOption === 'byok' && byokApiKey.trim()) extraEnvVars.MUMZWORLD_BYOK_API_KEY = byokApiKey.trim();
    if (description.trim()) extraEnvVars.MUMZWORLD_AGENT_DESCRIPTION = description.trim();
    if (tools.size > 0) extraEnvVars.MUMZWORLD_AGENT_TOOLS = Array.from(tools).join(',');
    const knowledge = [pdfFileName, excelFileName, websiteUrl].filter((v): v is string => Boolean(v));
    if (knowledge.length > 0) extraEnvVars.MUMZWORLD_KNOWLEDGE_SOURCES = knowledge.join(', ');

    // Non-secret tool configuration only — the matching tokens are declared as
    // manifest secrets below and injected from Floci, never sent from here.
    if (tools.has('Jira') && jiraProjectKey.trim()) {
      extraEnvVars.JIRA_PROJECT_KEY = jiraProjectKey.trim().toUpperCase();
    }
    if (tools.has('Slack') && slackChannel.trim()) {
      const channel = slackChannel.trim();
      extraEnvVars.SLACK_DEFAULT_CHANNEL = channel.startsWith('#') ? channel : `#${channel}`;
    }

    // Every secret the selected tools need, deduped — the deploy workflow
    // resolves these from Floci Secrets Manager into the container's env.
    const secrets = Array.from(
      new Set(Array.from(tools).flatMap((tool) => TOOL_SECRETS[tool] ?? [])),
    ).map((name) => ({ name }));

    const rawManifest = {
      apiVersion: 'platform.mumzworld.com/v1',
      kind: 'ApplicationInfrastructure',
      metadata: { application: sanitizedName, owner: displayName, branch: 'development', department },
      environment: { type: 'development' },
      application: { type: 'ai-agent', runtime: 'python', healthcheck: '/health', port: 8000 },
      compute: {},
      aws: {
        s3: [],
        sqs: [],
        dynamodb: [],
        database: tools.has('Database') ? { engine: 'postgres', size: 'small', public: false } : undefined,
        secrets,
      },
      // Outbound tool calls (agent -> Jira/Slack) work fine behind this.
      // Inbound (a Slack slash command or Jira webhook calling the agent)
      // would need `public: true` plus request-signature verification — a
      // deliberate follow-up, not an oversight.
      network: { public: false },
      observability: {},
      deployment: {},
    };

    try {
      const { workflowId } = await createAndDeployApplication(identityApi, {
        rawManifest,
        actorRole: 'DEVELOPER',
        template,
        deploy: true,
        extraEnvVars,
      });
      poll(workflowId);
    } catch (err) {
      setStatus({ phase: 'error', message: (err as Error).message });
    }
  };

  const isBusy = status.phase === 'submitting' || status.phase === 'polling';
  const submitDisabled =
    !sanitizedName ||
    !department ||
    isBusy ||
    nameCheck.phase === 'taken' ||
    (modelOption === 'byok' && !byokApiKey.trim());

  return (
    <PageShell>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <KindIcon kind="ai-agent" size={40} />
        <h1 style={{ fontSize: 20, fontWeight: 700, color: colors.text, margin: 0 }}>{kindConfig.title}</h1>
      </div>

      <div style={{ ...cardStyle, padding: 28, maxWidth: 560 }}>
        {status.phase === 'idle' || status.phase === 'submitting' || status.phase === 'error' ? (
          <>
            {status.phase === 'error' && (
              <div role="alert" style={{ marginBottom: 16, color: colors.danger, fontSize: 14 }}>
                {status.message}
              </div>
            )}

            <DepartmentSelect value={department} onChange={setDepartment} />

            <Field>
              <label style={labelStyle} htmlFor="agent-description">
                What should your agent do?
              </label>
              <textarea
                id="agent-description"
                style={{ ...inputStyle, resize: 'vertical' }}
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Analyse invoices and identify anomalies"
                aria-label="What should your agent do?"
              />
              {nameCheck.phase === 'checking' && (
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>Checking availability…</div>
              )}
              {nameCheck.phase === 'available' && (
                <div style={{ fontSize: 12, color: colors.success, marginTop: 4 }}>✓ Available</div>
              )}
              {nameCheck.phase === 'taken' && (
                <div style={{ fontSize: 12, color: colors.danger, marginTop: 4 }}>
                  ✗ This name is already taken — try rephrasing your description
                </div>
              )}
            </Field>

            <Field>
              <span style={labelStyle}>Knowledge</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <KnowledgeButton
                  ariaLabel="Upload PDF"
                  label="+ Upload PDF"
                  doneLabel={pdfFileName ? `✓ ${pdfFileName}` : undefined}
                  onClick={() => pdfInputRef.current?.click()}
                />
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept="application/pdf"
                  hidden
                  data-testid="upload-pdf-input"
                  onChange={(e) => setPdfFileName(e.target.files?.[0]?.name ?? null)}
                />
                <KnowledgeButton
                  ariaLabel="Upload Excel"
                  label="+ Upload Excel"
                  doneLabel={excelFileName ? `✓ ${excelFileName}` : undefined}
                  onClick={() => excelInputRef.current?.click()}
                />
                <input
                  ref={excelInputRef}
                  type="file"
                  accept=".xls,.xlsx,.csv"
                  hidden
                  data-testid="upload-excel-input"
                  onChange={(e) => setExcelFileName(e.target.files?.[0]?.name ?? null)}
                />
                {editingWebsite ? (
                  <input
                    autoFocus
                    aria-label="Website URL"
                    value={websiteDraft}
                    onChange={(e) => setWebsiteDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitWebsite();
                      if (e.key === 'Escape') setEditingWebsite(false);
                    }}
                    onBlur={commitWebsite}
                    placeholder="https://example.com"
                    style={inputStyle}
                  />
                ) : (
                  <KnowledgeButton
                    ariaLabel="Connect website"
                    label="+ Connect website"
                    doneLabel={websiteUrl ? `✓ ${websiteUrl}` : undefined}
                    onClick={() => {
                      setWebsiteDraft(websiteUrl ?? '');
                      setEditingWebsite(true);
                    }}
                  />
                )}
              </div>
            </Field>

            <Field>
              <span style={labelStyle}>Tools</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {TOOLS.map((tool) => (
                  <div key={tool}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                      <input
                        type="checkbox"
                        checked={tools.has(tool)}
                        onChange={() => toggleTool(tool)}
                        aria-label={tool}
                      />
                      {tool}
                    </label>

                    {/* Per-tool configuration, revealed only once its tool is
                        selected. Non-secret values only — the API tokens come
                        from Floci Secrets Manager, so there is no field here
                        to paste one into. */}
                    {tool === 'Jira' && tools.has('Jira') && (
                      <div style={{ margin: '8px 0 4px 24px' }}>
                        <label style={labelStyle} htmlFor="jira-project-key">
                          Jira project key
                        </label>
                        <input
                          id="jira-project-key"
                          style={inputStyle}
                          value={jiraProjectKey}
                          onChange={(e) => setJiraProjectKey(e.target.value)}
                          placeholder="SANDBOX"
                          aria-label="Jira project key"
                        />
                        <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                          Use a sandbox project — this agent files real tickets.
                        </div>
                      </div>
                    )}

                    {tool === 'Slack' && tools.has('Slack') && (
                      <div style={{ margin: '8px 0 4px 24px' }}>
                        <label style={labelStyle} htmlFor="slack-channel">
                          Slack channel
                        </label>
                        <input
                          id="slack-channel"
                          style={inputStyle}
                          value={slackChannel}
                          onChange={(e) => setSlackChannel(e.target.value)}
                          placeholder="#idp-agent-tests"
                          aria-label="Slack channel"
                        />
                        <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                          Use a dedicated test channel — this agent posts real messages.
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {(tools.has('Jira') || tools.has('Slack')) && (
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 10, lineHeight: 1.5 }}>
                  API tokens are injected by the platform from Secrets Manager — you don&apos;t need to
                  provide them. Tools are outbound only: this agent can post to Slack and file Jira
                  tickets, but cannot be triggered by them.
                </div>
              )}
            </Field>

            <Field>
              <span style={labelStyle}>AI Model</span>
              <div role="radiogroup" aria-label="AI Model" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {MODEL_OPTIONS.map((opt) => {
                  const selected = modelOption === opt.value;
                  return (
                    <div
                      key={opt.value}
                      role="radio"
                      aria-checked={selected}
                      aria-label={opt.label}
                      tabIndex={0}
                      onClick={() => setModelOption(opt.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setModelOption(opt.value);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '9px 12px',
                        borderRadius: 8,
                        border: `1px solid ${selected ? colors.primary : colors.border}`,
                        background: selected ? colors.primaryLight : colors.surface,
                        fontSize: 14,
                        color: colors.text,
                        cursor: 'pointer',
                      }}
                    >
                      <span>{opt.label}</span>
                      {selected && (
                        <span aria-hidden="true" style={{ color: colors.primary, fontWeight: 700 }}>
                          ✓
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {modelOption === 'byok' && (
                <div style={{ marginTop: 12 }}>
                  <label style={labelStyle} htmlFor="byok-api-key">
                    API Key
                  </label>
                  <input
                    id="byok-api-key"
                    type="password"
                    style={inputStyle}
                    value={byokApiKey}
                    onChange={(e) => setByokApiKey(e.target.value)}
                    placeholder="sk-..."
                    aria-label="API Key"
                  />
                </div>
              )}
            </Field>

            <button
              type="button"
              onClick={onSubmit}
              disabled={submitDisabled}
              aria-label="Deploy Agent"
              style={{
                width: '100%',
                padding: '11px 0',
                borderRadius: 8,
                border: 'none',
                background: submitDisabled ? colors.borderLight : colors.primary,
                color: submitDisabled ? colors.textMuted : '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: submitDisabled ? 'default' : 'pointer',
              }}
            >
              {status.phase === 'submitting' ? 'Starting…' : 'Deploy Agent 🚀'}
            </button>
          </>
        ) : status.phase === 'polling' ? (
          <ProgressStepper stages={PROGRESS_STAGES} currentStage={stageIndexForStatus(status.status)} />
        ) : status.phase === 'done' ? (
          (() => {
            const result = status.result;
            const finalStatus = String(result?.status ?? '');
            if (finalStatus === 'DEPLOYED') {
              const liveUrl = String(result?.liveUrl ?? '');
              return (
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: colors.text, marginBottom: 4 }}>
                    {String(result?.repoFullName ?? sanitizedName).split('/').pop()}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ color: colors.success, fontWeight: 600, fontSize: 14 }}>● Running</span>
                  </div>
                  {liveUrl && (
                    <div style={{ marginBottom: 20, fontSize: 14 }}>
                      <a href={liveUrl} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>
                        {liveUrl}
                      </a>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12 }}>
                    {liveUrl && (
                      <a
                        href={liveUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          padding: '10px 18px',
                          borderRadius: 8,
                          background: colors.primary,
                          color: '#fff',
                          fontSize: 14,
                          fontWeight: 600,
                          textDecoration: 'none',
                        }}
                      >
                        Open Application
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate('/applications')}
                      style={{
                        padding: '10px 18px',
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`,
                        background: colors.surface,
                        color: colors.text,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      View Details
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      style={{
                        padding: '10px 18px',
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`,
                        background: colors.surface,
                        color: colors.text,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Create New
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: colors.danger, marginBottom: 8 }}>
                  Deployment failed
                </div>
                <div style={{ fontSize: 14, color: colors.textMuted, marginBottom: 20 }}>
                  {String(result?.failureMessage ?? 'An unknown error occurred.')}
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => setStatus({ phase: 'idle' })}
                    style={{
                      padding: '10px 18px',
                      borderRadius: 8,
                      border: 'none',
                      background: colors.primary,
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Retry
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/applications')}
                    style={{
                      padding: '10px 18px',
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`,
                      background: colors.surface,
                      color: colors.text,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    View Details
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    style={{
                      padding: '10px 18px',
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`,
                      background: colors.surface,
                      color: colors.text,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Create New
                  </button>
                </div>
              </div>
            );
          })()
        ) : null}
      </div>
    </PageShell>
  );
}
